import { useCallback, useEffect, useRef, useState } from "react";

import { debug } from "@/utils/debug/logger";

// Google gview sometimes returns a blank frame (Nov 2024); remounting it helps. A direct URL is the
// backend streaming the file: Chrome fires `load` only after the last byte, so a remount would restart
// the download from zero. Direct URLs therefore never fail on a timer, they only say "still loading".
const GOOGLE_VIEWER_RETRY_MS = 8000;
const MAX_GOOGLE_VIEWER_REMOUNTS = 3;
const STILL_LOADING_AFTER_MS = 15000;

export type PdfTimeoutAction = "remount" | "showStillLoading" | "fail";

export const pdfTimeoutDelayMs = (useGoogleViewer: boolean): number =>
  useGoogleViewer ? GOOGLE_VIEWER_RETRY_MS : STILL_LOADING_AFTER_MS;

export const pdfTimeoutAction = ({
  useGoogleViewer,
  attempts,
}: {
  useGoogleViewer: boolean;
  attempts: number;
}): PdfTimeoutAction => {
  if (!useGoogleViewer) {
    return "showStillLoading";
  }
  return attempts < MAX_GOOGLE_VIEWER_REMOUNTS ? "remount" : "fail";
};

export interface PdfFrameLoadSignal {
  /** window.length of the frame's contentWindow, i.e. its number of child frames. */
  childFrameCount: number;
}

// Browser PDF plugins render the document directly into the frame, so a
// successfully loaded PDF usually reports zero child frames. Child frame
// count carries no signal about load failure and must never be treated as
// an empty/failed PDF.
export const isPdfFrameLoadUsable = ({ childFrameCount }: PdfFrameLoadSignal): boolean => childFrameCount >= 0;

interface PDFLoadingHookProps {
  onSuccess: () => void;
  onError: (error: Error) => void;
  onStillLoading?: () => void;
  useGoogleViewer: boolean;
  iframeRef: React.RefObject<HTMLIFrameElement> | React.RefObject<HTMLEmbedElement>;
}

interface LoadingState {
  isLoading: boolean;
  refreshKey: number;
  timeoutAttemptCount: number;
  // Timer is done: the direct download was reported slow, or the Google viewer failed.
  timedOut: boolean;
}

export const usePDFLoading = ({
  onSuccess,
  onError,
  onStillLoading,
  useGoogleViewer,
  iframeRef,
}: PDFLoadingHookProps) => {
  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: true,
    refreshKey: 0,
    timeoutAttemptCount: 0,
    timedOut: false,
  });

  const isLoadedRef = useRef(false);

  // One timer per attempt. Depends only on the attempt, so parent re-renders don't restart it.
  useEffect(() => {
    if (!loadingState.isLoading || loadingState.timedOut) {
      return;
    }

    const timer = setTimeout(() => {
      if (isLoadedRef.current) {
        return;
      }
      const action = pdfTimeoutAction({ useGoogleViewer, attempts: loadingState.timeoutAttemptCount });
      debug.log("usePDFLoading", `Timeout after attempt ${loadingState.timeoutAttemptCount}: ${action}`);

      if (action === "remount") {
        setLoadingState((prev) => ({
          ...prev,
          refreshKey: prev.refreshKey + 1,
          timeoutAttemptCount: prev.timeoutAttemptCount + 1,
        }));
      } else if (action === "showStillLoading") {
        setLoadingState((prev) => ({ ...prev, timedOut: true }));
        onStillLoading?.();
      } else {
        setLoadingState((prev) => ({ ...prev, isLoading: false, timedOut: true }));
        onError(new Error(`PDF loading failed after ${loadingState.timeoutAttemptCount} attempts`));
      }
    }, pdfTimeoutDelayMs(useGoogleViewer));

    return () => clearTimeout(timer);
  }, [
    loadingState.isLoading,
    loadingState.timedOut,
    loadingState.timeoutAttemptCount,
    useGoogleViewer,
    onError,
    onStillLoading,
  ]);

  const handleLoad = useCallback(() => {
    // Safari's <embed> never fires load; PDFViewerFrame calls this after 100 ms instead.
    if (iframeRef.current instanceof HTMLEmbedElement) {
      debug.log("usePDFLoading", "HTMLEmbedElement detected, assuming successful load.");
      isLoadedRef.current = true;
      onSuccess();
      setLoadingState((prev) => ({ ...prev, isLoading: false }));
      return;
    }
    const contentWindow = iframeRef.current?.contentWindow;
    if (contentWindow && isPdfFrameLoadUsable({ childFrameCount: contentWindow.length })) {
      debug.log("usePDFLoading", contentWindow.length, "assuming successful load.");
      onSuccess();
    }

    if (!isLoadedRef.current) {
      isLoadedRef.current = true;
      setLoadingState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [iframeRef, onSuccess]);

  return {
    isLoading: loadingState.isLoading,
    refreshKey: loadingState.refreshKey,
    handleLoad,
  };
};
